<script setup>
import { ref, onBeforeUnmount } from 'vue'
import { Share2, Link2, Check } from 'lucide-vue-next'

/**
 * Share this profile — and actually share it.
 *
 * The previous version had four social buttons that emitted an event no parent
 * ever listened for, so all four did nothing at all. This uses the browser's own
 * share sheet where there is one (which is where a parent forwarding a profile
 * to a partner actually is: their phone), and falls back to copying the link.
 */
const props = defineProps({
  tutorName: { type: String, default: '' },
})

const copied = ref(false)
let resetTimer

const shareUrl = () => (typeof window === 'undefined' ? '' : window.location.href)

const share = async () => {
  const url = shareUrl()
  const title = props.tutorName ? `${props.tutorName} on Bindle` : 'A tutor on Bindle'

  if (navigator.share) {
    try {
      await navigator.share({ title, url })
      return
    } catch {
      /* Dismissing the sheet is not an error; fall through to copying. */
    }
  }

  await copy()
}

const copy = async () => {
  try {
    await navigator.clipboard.writeText(shareUrl())
    copied.value = true
    clearTimeout(resetTimer)
    resetTimer = setTimeout(() => { copied.value = false }, 2400)
  } catch {
    /* Clipboard blocked (insecure context, or refused). The link is in the
       address bar either way, so there is nothing useful to say here. */
  }
}

onBeforeUnmount(() => clearTimeout(resetTimer))
</script>

<template>
  <section class="surface-card p-6">
    <h2 class="font-sans text-base font-bold text-ink">Share this profile</h2>
    <div class="mt-4 flex flex-wrap gap-2">
      <button
        type="button"
        class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md bg-brand px-4 text-sm font-semibold text-brand-contrast transition-colors hover:bg-brand-hover"
        @click="share"
      >
        <Share2 class="size-4" /> Share
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-line px-4 text-sm font-semibold text-ink transition-colors hover:bg-surface-2"
        :aria-label="copied ? 'Link copied' : 'Copy link'"
        @click="copy"
      >
        <Check v-if="copied" class="size-4 text-success" />
        <Link2 v-else class="size-4" />
        <span>{{ copied ? 'Copied' : 'Copy link' }}</span>
      </button>
      <!--
        The email option was a `mailto:` link. It opens whatever desktop mail
        client the machine has registered, which for most people is nothing at
        all, so the button appeared to do nothing. The share sheet already offers
        email wherever the device actually has it.
      -->
    </div>
  </section>
</template>
